import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { OpenHoursInput } from "@/app/Offices/Components/OpenHoursInput";
import { OpenHoursViewState } from "@/app/Offices/Components/OpenHoursViewState";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { Link } from "@/components/catalyst/link";
import {
  fromOpenDaysSchema,
  toOpenDaysSchema,
} from "@/app/Suppliers/Utils/mapClosedDays";
import { ClosedDatesForm } from "@/app/Suppliers/Components/ClosedDatesForm";
import { IllustrationCalendar } from "@/assets/Illustrations";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierOfficeMutation } from "@/app/Offices/GraphQL/supplierOfficeMutations";
import { useOfficeRecord } from "@/app/Offices/Hooks/useOfficeRecord";
import { useTranslations } from "use-intl";

export function OfficeOpenHoursScreen() {
  const record = useOfficeRecord();
  const { mutateAsync } = useGqlMutation(updateSupplierOfficeMutation);

  return (
    <div className="space-y-6 py-6">
      <RecordScreenFormCard
        enableReinitialize={true}
        onSubmit={async (values) => {
          await mutateAsync({
            id: record.id,
            input: {
              departure_hours: values.custom_hours
                ? values.departure_hours
                : null,
            },
          });
        }}
        initialValues={{
          custom_hours: record.departure_hours !== null,
          departure_hours:
            record.departure_hours ?? record.supplier.departure_hours,
        }}
        title="Departure hours"
        view={<InheritedFromSupplierBanner type="departure_hours" />}
      >
        <SupplierOfficeOpenHoursForm name={`departure_hours`} />
      </RecordScreenFormCard>

      <RecordScreenFormCard
        enableReinitialize={true}
        onSubmit={async (values, { resetForm }) => {
          await mutateAsync({
            id: record.id,
            input: {
              delivery_hours: values.custom_hours
                ? values.delivery_hours
                : null,
            },
          });
          resetForm();
        }}
        initialValues={{
          custom_hours: record.delivery_hours !== null,
          delivery_hours:
            record.delivery_hours ?? record.supplier.delivery_hours,
        }}
        title="Delivery hours"
        view={<InheritedFromSupplierBanner type="delivery_hours" />}
      >
        <SupplierOfficeOpenHoursForm name={`delivery_hours`} />
      </RecordScreenFormCard>

      <RecordScreenFormCard
        onSubmit={async (values) => {
          const closedDates = toOpenDaysSchema(values.closed_dates);
          await mutateAsync({
            id: record.id,
            input: {
              closed_dates: closedDates,
            },
          });
        }}
        title="Closed days"
        initialValues={{
          closed_dates: fromOpenDaysSchema(record.closed_dates),
        }}
      >
        <ClosedDatesForm
          emptyState={
            <EmptyState
              Icon={IllustrationCalendar}
              header="No closed dates"
              subheader="Add date(s) you want to close this office on"
            />
          }
        />
      </RecordScreenFormCard>
    </div>
  );
}

export function SupplierOfficeOpenHoursForm({ name }: { name: string }) {
  const { values } = useFormState();

  return (
    <div className="col-span-full">
      <div className="mb-4">
        <SwitchInput
          name="custom_hours"
          label="Override supplier hours"
          className="col-span-full"
        />
      </div>
      <OpenHoursInput
        prefix={name}
        label={
          name === "departure_hours" ? "Departure Hours" : "Delivery Hours"
        }
        className="col-span-full"
        disabled={!values.custom_hours}
      />
    </div>
  );
}

function InheritedFromSupplierBanner({
  type,
}: {
  type: "delivery_hours" | "departure_hours";
}) {
  const t = useTranslations("office.openHours");
  const record = useOfficeRecord();

  if (record[type] !== null) {
    return (
      <OpenHoursViewState
        openHours={record[type]}
        parentOpenHours={record.supplier[type]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <OpenHoursViewState openHours={record.supplier[type]} />

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start">
          <div className="shrink-0">
            <InformationCircleIcon
              className="h-5 w-5 text-blue-500"
              aria-hidden="true"
            />
          </div>
          <div className="ml-3 flex-1">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p className="text-sm font-medium text-blue-800">
                The {t(type)} are inherited from the supplier.
              </p>
              <Link
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to={"/$tenant/$orgId/supplier/{-$supplierId}/settings/offices"}
                params={(prev) => prev}
                search={{ supplier: record.supplier.id }}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-600"
              >
                View supplier hours
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
